import React from 'react';
import DocumentPage, {
  Section,
  SubSection,
  Paragraph,
  BulletList,
} from './DocumentPage';
import CalorVesselsNotice from './CalorVesselsNotice';

const TAG_COLOUR_ROWS = [
  {
    colour: 'Green',
    swatch: 'bg-emerald-500',
    status: 'Inspection Required',
    issuedBy: 'Authorised Vessel Inspector',
    trigger: 'Pre-Inspection Form completed',
    appSection: 'Section 7',
  },
  {
    colour: 'Orange',
    swatch: 'bg-orange-500',
    status: 'Nothing Required (In-Test, No Remedial Work)',
    issuedBy: 'Authorised Vessel Inspector',
    trigger: 'Vessel confirmed in-test, current certificate valid',
    appSection: 'Section 8',
  },
  {
    colour: 'Red',
    swatch: 'bg-red-600',
    status: 'Vessel Scrapped / Failure',
    issuedBy: 'Authorised Vessel Inspector',
    trigger: 'Vessel Failure Report with photographic evidence',
    appSection: 'Section 9',
  },
  {
    colour: 'White',
    swatch: 'bg-white',
    status: 'Post-Paint Multi-Stage QC (Painted → Valved → Ready for Despatch)',
    issuedBy: 'Coating Inspector (tag attached); stages signed by respective role holders',
    trigger: 'Paint QC Form → Vessel Certification → Final QC Form',
    appSection: 'Section 10',
  },
];

const WHITE_TAG_STAGES = [
  {
    stage: '1',
    label: 'Paint Quality Check',
    form: 'Paint Quality Inspection Form',
    initialsBy: 'Coating Inspector',
    statusAfter: "'Painted'",
  },
  {
    stage: '2',
    label: 'Valve Installation & Certification',
    form: 'Vessel Certification Form',
    initialsBy: 'Authorised Vessel Inspector / Senior Engineer',
    statusAfter: "'Valved'",
  },
  {
    stage: '3',
    label: 'Final QC Check',
    form: 'Final QC Checklist Form',
    initialsBy: 'QC Inspector',
    statusAfter: "'Ready for Despatch'",
  },
];

const ACCESS_LEVEL_ROWS = [
  {
    role: 'Operative / Handler',
    permissions: 'Read-only view of vessel status. No ability to create, edit or delete records.',
  },
  {
    role: 'Coating Inspector',
    permissions: 'Submit Paint Quality Inspection Form; view vessel status; attach photographs to own submissions.',
  },
  {
    role: 'QC Inspector',
    permissions: 'Submit Final QC Checklist Form; view vessel status.',
  },
  {
    role: 'Authorised Vessel Inspector',
    permissions: 'Create vessel records; issue Green / Orange / Red tags; submit Pre-Inspection Form, Vessel Certification Form, and Vessel Failure Report; attach photographic evidence.',
  },
  {
    role: 'Quality Manager',
    permissions: 'All inspector permissions plus authorisation of manual-override justifications, review of non-conformances, and approval of user-access change requests.',
  },
  {
    role: 'Director',
    permissions: 'Sole authority to approve a manual override of an automated vessel status (in conjunction with the Quality Manager).',
  },
  {
    role: 'IT / System Administrator',
    permissions: 'User account creation, modification and deletion (on written instruction of the Quality Manager); backup administration and restore testing. No authority to edit vessel records or audit trail entries.',
  },
];

const VesselTaggingPolicyDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="Vessel Tagging & Digital Application Policy"
    documentHeading="Vessel Tagging & Digital Application Policy"
    includeDefaultMeta={false}
    meta={[
      ['Company', 'Pressure Vessel Services Limited'],
      ['Document Reference', 'PVS-POL-VT-001'],
      ['Version', '1.0'],
      ['Date of Issue', '21st April 2026'],
      ['Next Review Date', '21st April 2027'],
      ['Review Period', '12 months'],
      ['Prepared by', 'Pressure Vessel Services Limited'],
      ['Approved by', 'Board of Directors'],
      ['Applicability', 'All PVS personnel, subcontractors and agency staff handling pressure vessels on PVS premises'],
    ]}
    acknowledgment={{
      documentName: 'Vessel Tagging & Digital Application Policy',
      documentReference: 'PVS-POL-VT-001',
      processActivity: 'Vessel Tagging & Digital Vessel Inventory Application Use',
      versionNumber: '1.0',
      dateOfIssue: '21st April 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <CalorVesselsNotice />

    <Section number="1" title="Purpose and Scope">
      <Paragraph>
        This policy establishes the mandatory procedures and controls governing the physical
        tagging of pressure vessels and the use of the digital Vessel Inventory Application at
        Pressure Vessel Services Limited (PVS). It defines the responsibilities, workflows, and
        quality controls required to ensure that every vessel processed through the facility
        maintains a clearly identifiable, accurate, and auditable status at all stages of its
        lifecycle.
      </Paragraph>
      <Paragraph className="mt-2">
        This policy applies to all personnel involved in the receipt, handling, inspection, coating
        and despatch of pressure vessels at PVS facilities, including subcontractors and agency
        staff operating under PVS supervision.
      </Paragraph>
    </Section>

    <Section number="2" title="Definitions">
      <BulletList
        items={[
          <><strong>Authorised Vessel Inspector:</strong> an individual formally trained, assessed, and authorised in writing by the Quality Manager to determine vessel status and issue Green, Orange or Red tags.</>,
          <><strong>Coating Inspector:</strong> an individual authorised to inspect and record the outcome of the coating process and attach the White Tag.</>,
          <><strong>QC Inspector:</strong> an individual authorised to complete the Final QC Checklist and release a vessel for despatch.</>,
          <><strong>Vessel Inventory Application:</strong> the PVS digital system-of-record for all vessels processed at the facility, holding vessel identity, status, forms and audit trail.</>,
          <><strong>Through Test:</strong> a full pressure test and internal/external inspection as defined in the Written Scheme of Examination.</>,
        ]}
      />
    </Section>

    <Section number="3" title="Roles and Responsibilities">
      <BulletList
        items={[
          <><strong>Board of Directors:</strong> approve this policy, authorise any manual override of an automated vessel status, and review non-conformances arising under this policy.</>,
          <><strong>Quality Manager:</strong> owns the policy, maintains authorised inspector lists, authorises user-access changes, investigates non-conformances, and co-signs any manual override.</>,
          <><strong>Authorised Vessel Inspectors:</strong> determine vessel status, complete the applicable forms, apply the correct tag, and ensure the digital record and physical tag remain in agreement.</>,
          <><strong>Coating Inspector:</strong> attach the White Tag on completion of coating, complete the Paint Quality Inspection Form, and initial Stage 1 of the White Tag.</>,
          <><strong>QC Inspector:</strong> complete the Final QC Checklist Form and initial the final stage of the White Tag prior to despatch.</>,
          <><strong>Operatives / Handlers:</strong> only handle vessels in accordance with their current tag and status, and escalate any discrepancy to an Authorised Vessel Inspector.</>,
          <><strong>IT / System Administrator:</strong> manage user accounts, backups and system availability in accordance with Section 11.</>,
        ]}
      />
    </Section>

    <Section number="4" title="General Requirements">
      <BulletList
        items={[
          'Every vessel on PVS premises shall carry a physical tag corresponding to the current status held in the Vessel Inventory Application.',
          'The physical tag and the digital record must agree at all times. Any discrepancy constitutes a non-conformance (Section 12).',
          'Tags shall be printed from controlled stock, legibly filled in using an indelible marker, and securely attached to the vessel using the designated tagging point.',
          'A vessel shall not be moved to the next process stage (e.g. pyrolysis, shotblasting, spraybooth, despatch) until its tag and digital status permit that movement.',
          'Tags shall not be reused between vessels. Tags removed from a scrapped vessel shall be destroyed.',
        ]}
      />
    </Section>

    <Section number="5" title="Vessel Tag Colour Reference Summary">
      <Paragraph>
        The following table provides an at-a-glance reference for all vessel tag types in use at
        PVS. Full procedural requirements for each tag type are detailed in Sections 6 through 10.
      </Paragraph>
      <div className="overflow-x-auto rounded-lg shadow-md mt-4">
        <table className="min-w-full">
          <thead>
            <tr className="bg-pvs-card-light">
              <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Tag Colour</th>
              <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Vessel Status</th>
              <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Issued By</th>
              <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Trigger / Required Record</th>
              <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Reference</th>
            </tr>
          </thead>
          <tbody>
            {TAG_COLOUR_ROWS.map((row, i) => (
              <tr key={row.colour} className={i % 2 === 0 ? 'bg-white/5' : ''}>
                <td className="py-3 px-4 text-pvs-text-muted align-top">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-4 h-4 rounded-sm border border-pvs-border ${row.swatch}`} aria-hidden="true" />
                    <span className="font-semibold">{row.colour}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-pvs-text-muted align-top">{row.status}</td>
                <td className="py-3 px-4 text-pvs-text-muted align-top">{row.issuedBy}</td>
                <td className="py-3 px-4 text-pvs-text-muted align-top">{row.trigger}</td>
                <td className="py-3 px-4 text-pvs-text-muted align-top">{row.appSection}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>

    <Section number="6" title="Vessel Arrival, Unloading, and Holding Area">
      <SubSection title="6.1 Unloading">
        <Paragraph>
          Upon arrival at the PVS facility, all vessels shall be unloaded and transferred to the
          designated vessel holding area. No vessel shall be placed directly into the active
          working area prior to being logged into the Vessel Inventory Application and having its
          status determined by an Authorised Vessel Inspector.
        </Paragraph>
      </SubSection>
      <SubSection title="6.2 Holding Area Requirements">
        <Paragraph>
          The vessel holding area shall be maintained in a clean, safe, and organised condition.
          Vessels in the holding area shall be treated as un-inspected until formally logged and
          tagged. The following conditions apply to all vessels in the holding area:
        </Paragraph>
        <BulletList
          items={[
            'All vessels must remain physically segregated from vessels that have been inspected and tagged.',
            'No work shall be carried out on a vessel in the holding area prior to its status being determined, unless a safety intervention is required (see Section 9).',
            'If a vessel is observed to present an immediate safety risk whilst in the holding area, handling staff must notify an Authorised Vessel Inspector immediately. The vessel-failure process (Section 9) shall be initiated without delay.',
          ]}
        />
      </SubSection>
      <SubSection title="6.3 Logging into the Vessel Inventory Application">
        <Paragraph>
          An Authorised Vessel Inspector shall log each vessel into the Vessel Inventory
          Application at the earliest practicable opportunity following arrival, recording as a
          minimum: the vessel serial number, customer/owner, water capacity, year of manufacture,
          last test date (where known), date of receipt at PVS, and initial visual observations.
        </Paragraph>
      </SubSection>
    </Section>

    <Section number="7" title="Green Tag — Inspection Required">
      <SubSection title="7.1 Criteria for Issue">
        <Paragraph>
          A green tag shall be issued when an Authorised Vessel Inspector has determined that a
          vessel requires a through test (full pressure test and inspection). This determination
          must be based on a physical assessment of the vessel and recorded formally in the
          application.
        </Paragraph>
      </SubSection>
      <SubSection title="7.2 Pre-Inspection Form Requirement">
        <Paragraph>
          Prior to issuing a green tag, the Authorised Vessel Inspector must complete the
          <strong>Pre-Inspection Form</strong> within the Vessel Inventory Application.
        </Paragraph>
      </SubSection>
      <SubSection title="7.3 Automated Status Update">
        <Paragraph>
          Upon submission of the completed Pre-Inspection Form, the Vessel Inventory Application
          shall automatically update the vessel status to <strong>'Inspection Required'</strong>.
          This automated status change constitutes the formal record of the inspector's
          determination and must not be manually overridden except by an Authorised Vessel
          Inspector with documented justification, in accordance with Section 11.4.
        </Paragraph>
      </SubSection>
      <SubSection title="7.4 Physical Tag Application">
        <Paragraph>
          Following the automated status update, a green tag shall be physically affixed to the
          vessel by the Authorised Vessel Inspector. The green tag must:
        </Paragraph>
        <BulletList
          items={[
            'Be clearly visible and securely attached to the vessel.',
            "Include the inspector's identification reference and the date of assessment.",
            'Not be applied prior to completion of the Pre-Inspection Form.',
          ]}
        />
      </SubSection>
    </Section>

    <Section number="8" title="Orange Tag — Nothing Required">
      <SubSection title="8.1 Criteria for Issue">
        <Paragraph>
          An orange tag shall be issued when an Authorised Vessel Inspector has determined that a
          vessel currently in test does not require any remedial work or further testing at this
          time. The vessel must have passed its existing certification period and be confirmed as
          currently in test.
        </Paragraph>
      </SubSection>
      <SubSection title="8.2 Status Update Requirement">
        <Paragraph>
          The Authorised Vessel Inspector shall update the vessel status in the Vessel Inventory
          Application to <strong>'Nothing Required'</strong> before or at the time of affixing the
          orange tag. The status update must include:
        </Paragraph>
        <BulletList
          items={[
            'Confirmation that the vessel is in test and the current test certificate remains valid.',
            "The date of the inspector's determination.",
            "The inspector's name and authorisation reference.",
          ]}
        />
      </SubSection>
      <SubSection title="8.3 Physical Tag Application">
        <Paragraph>
          The orange tag is applied by the Authorised Vessel Inspector following completion of the
          status update in the application. The tag must be clearly visible and securely affixed to
          the vessel.
        </Paragraph>
      </SubSection>
    </Section>

    <Section number="9" title="Red Tag — Vessel Scrapped / Failure">
      <SubSection title="9.1 Criteria for Issue">
        <Paragraph>
          A red tag shall be issued when an Authorised Vessel Inspector has determined at any stage
          of the process — including whilst in the holding area — that a vessel is unfit for
          purpose or presents a danger to personnel, equipment, or the public. There is no minimum
          process stage at which this determination can be made; an Authorised Vessel Inspector may
          issue a red tag at any point.
        </Paragraph>
      </SubSection>
      <SubSection title="9.2 Vessel Failure Report">
        <Paragraph>
          Upon determination that a vessel requires a red tag, the Authorised Vessel Inspector must
          immediately complete a <strong>Vessel Failure Report</strong> within the Vessel
          Inventory Application.
        </Paragraph>
      </SubSection>
      <SubSection title="9.3 Photographic Evidence">
        <Paragraph>
          Photographic evidence of the condition of the vessel shall be captured and attached to
          the Vessel Failure Report within the Vessel Inventory Application. A minimum of one
          photograph showing the defect or dangerous condition is required. Where multiple defects
          are present, each shall be individually documented with a corresponding photograph.
          Photographs must:
        </Paragraph>
        <BulletList
          items={[
            'Be clear and of sufficient resolution to identify the defect.',
            'Be timestamped where the device permits.',
            'Be uploaded to the Vessel Inventory Application record without alteration, crop or post-processing that would obscure the defect.',
          ]}
        />
      </SubSection>
      <SubSection title="9.4 Making the Vessel Safe">
        <Paragraph>
          Where a dangerous vessel presents an immediate risk to personnel or equipment, the
          Authorised Vessel Inspector shall authorise and oversee such measures as are necessary to
          make the vessel safe prior to applying the red tag. Actions taken must be documented in
          the Vessel Failure Report. Appropriate making-safe measures may include, but are not
          limited to:
        </Paragraph>
        <BulletList
          items={[
            'Venting residual pressure (only by competent, authorised personnel).',
            'Physical segregation of the vessel from the working area.',
            'Conspicuous hazard marking pending red-tag application.',
          ]}
        />
      </SubSection>
      <SubSection title="9.5 Red Tag Application">
        <Paragraph>
          Following completion of the Vessel Failure Report and, where applicable, making the
          vessel safe, the red tag shall be applied by the Authorised Vessel Inspector. A
          red-tagged vessel must be:
        </Paragraph>
        <BulletList
          items={[
            'Clearly segregated from all other vessels in the facility.',
            'Not returned to service, released to the customer, or processed further, except for disposal or destruction.',
            'Not de-tagged or have its status changed without the written authorisation of the Quality Manager and a second Authorised Vessel Inspector.',
          ]}
        />
      </SubSection>
    </Section>

    <Section number="10" title="White Tag — Post-Paint Multi-Stage QC">
      <SubSection title="10.1 Purpose">
        <Paragraph>
          The white tag is a multi-stage quality-control record applied to a vessel following
          completion of the painting process. It provides a visual reference for all personnel on
          the production floor indicating which QC stages have been completed and which remain
          outstanding. The white tag supports the digital audit trail maintained in the Vessel
          Inventory Application.
        </Paragraph>
      </SubSection>
      <SubSection title="10.2 Attachment of the White Tag">
        <Paragraph>
          The white tag shall be attached to the vessel by the Coating Inspector upon completion of
          the painting process. No other individual is authorised to attach the white tag. At the
          point of attachment, the tag will contain three empty initials boxes corresponding to the
          stages below.
        </Paragraph>
        <div className="overflow-x-auto rounded-lg shadow-md mt-4">
          <table className="min-w-full">
            <thead>
              <tr className="bg-pvs-card-light">
                <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Stage</th>
                <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Check</th>
                <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Form (Vessel Inventory Application)</th>
                <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Initials By</th>
                <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Automated Status After Completion</th>
              </tr>
            </thead>
            <tbody>
              {WHITE_TAG_STAGES.map((row, i) => (
                <tr key={row.stage} className={i % 2 === 0 ? 'bg-white/5' : ''}>
                  <td className="py-3 px-4 text-pvs-text-muted align-top font-semibold">{row.stage}</td>
                  <td className="py-3 px-4 text-pvs-text-muted align-top">{row.label}</td>
                  <td className="py-3 px-4 text-pvs-text-muted align-top">{row.form}</td>
                  <td className="py-3 px-4 text-pvs-text-muted align-top">{row.initialsBy}</td>
                  <td className="py-3 px-4 text-pvs-text-muted align-top">{row.statusAfter}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SubSection>
      <SubSection title="10.3 Stage Completion Requirements">
        <Paragraph>
          Each stage of the white tag process must be completed in the sequence shown above. The
          person responsible for each stage must:
        </Paragraph>
        <BulletList
          items={[
            'Complete the corresponding form or check within the Vessel Inventory Application.',
            'Initial the corresponding box on the physical white tag upon completion of that stage.',
            'Not initial any box other than the one corresponding to their designated role.',
          ]}
        />
      </SubSection>
      <SubSection title="10.4 Automated Vessel Status Update">
        <Paragraph>
          The Vessel Inventory Application shall automatically update the vessel status each time a
          stage form is completed and submitted. The sequence of automated status updates is:
        </Paragraph>
        <BulletList
          items={[
            <>Completion of the Paint Quality Inspection Form → Status updated to <strong>'Painted'</strong>.</>,
            <>Completion of the Vessel Certification Form → Status updated to <strong>'Valved'</strong>.</>,
            <>Completion of the Final QC Check Form → Status updated to <strong>'Ready for Despatch'</strong>.</>,
          ]}
        />
      </SubSection>
      <SubSection title="10.5 Incomplete White Tags">
        <Paragraph>
          A vessel with a white tag on which one or more boxes have not been initialled must not be
          released for despatch. The Vessel Inventory Application shall prevent release of a vessel
          whose digital record does not reflect completion of all three QC stages. Any attempt to
          release a vessel with incomplete QC stages must be escalated to the Quality Manager.
        </Paragraph>
      </SubSection>
    </Section>

    <Section number="11" title="Vessel Inventory Application — Controls and Compliance">
      <SubSection title="11.1 System Access">
        <Paragraph>
          Access to the Vessel Inventory Application shall be managed by the IT / System
          Administrator. User accounts shall be created, modified and deleted only on the written
          instruction of the Quality Manager. User access levels shall reflect the minimum
          permissions required for each role. The following access levels are defined:
        </Paragraph>
        <div className="overflow-x-auto rounded-lg shadow-md mt-4">
          <table className="min-w-full">
            <thead>
              <tr className="bg-pvs-card-light">
                <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Role</th>
                <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Permissions</th>
              </tr>
            </thead>
            <tbody>
              {ACCESS_LEVEL_ROWS.map((row, i) => (
                <tr key={row.role} className={i % 2 === 0 ? 'bg-white/5' : ''}>
                  <td className="py-3 px-4 text-pvs-text-muted align-top font-semibold w-1/3">{row.role}</td>
                  <td className="py-3 px-4 text-pvs-text-muted align-top">{row.permissions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SubSection>
      <SubSection title="11.2 Audit Trail">
        <Paragraph>
          The Vessel Inventory Application must maintain a full, immutable audit trail of all
          status changes. The audit trail must record:
        </Paragraph>
        <BulletList
          items={[
            'The unique vessel identifier to which the change relates.',
            'The previous status value.',
            'The new status value.',
            'The user ID of the individual making the change.',
            'The date and time of the change (UTC, system-generated).',
          ]}
        />
        <Paragraph className="mt-2">
          Audit-trail records must not be editable by any user, including administrators. Any
          system or database administrator access that could permit modification of audit-trail
          records must be formally documented and controlled.
        </Paragraph>
      </SubSection>
      <SubSection title="11.3 Data Integrity and Backup">
        <Paragraph>
          The Vessel Inventory Application data must be backed up at a minimum of daily intervals.
          Backup records must be retained for a period of not less than <strong>10 years</strong>.
          Backup integrity shall be verified by the IT / System Administrator at a minimum of
          monthly intervals, with results recorded. In the event of a system failure, the Recovery
          Time Objective (RTO) must not exceed <strong>one working day</strong>.
        </Paragraph>
      </SubSection>
      <SubSection title="11.4 Manual Override">
        <Paragraph>
          In exceptional circumstances, an automated status change may require manual correction.
          Any manual override of a vessel status within the application must:
        </Paragraph>
        <BulletList
          items={[
            'Be authorised in writing by a Director and co-signed by the Quality Manager.',
            'Include a documented justification within the vessel record.',
            'Be captured in the audit trail with user ID, date and time.',
            'Be reviewed at the next management / quality review meeting.',
          ]}
        />
      </SubSection>
    </Section>

    <Section number="12" title="Non-Conformance and Corrective Action">
      <Paragraph>
        Any departure or deviation from the requirements of this policy shall be treated as a
        non-conformance and will be addressed by the Directors on a case-by-case basis in
        accordance with the PVS QMS non-conformance procedure. All non-conformances shall be
        logged, investigated, and closed out with documented corrective and, where appropriate,
        preventive actions.
      </Paragraph>
      <Paragraph className="mt-2">
        Examples of reportable non-conformances under this policy include, but are not limited to:
      </Paragraph>
      <BulletList
        items={[
          'A vessel tag applied by an unauthorised individual.',
          'A vessel status updated in the application without a corresponding completed form.',
          'A discrepancy between the physical tag on a vessel and its status in the Vessel Inventory Application.',
          'A vessel despatched without all three white-tag QC stages completed.',
          'A Vessel Failure Report submitted without photographic evidence.',
          'Any system access or data modification outside the permissions defined in Section 11.1.',
        ]}
      />
    </Section>

    <Section number="13" title="Training and Competence">
      <Paragraph>
        All personnel with responsibilities under this policy must have received documented
        training appropriate to their role prior to undertaking any activities governed by this
        policy. Training records shall be maintained in the <strong>PVS Master Training &amp;
        Competency Matrix</strong>.
      </Paragraph>
      <BulletList
        items={[
          'Authorised Vessel Inspectors must be formally assessed and certified by an approved training body and recorded on the PVS Inspector Authorisation Register.',
          'Coating Inspectors and QC Inspectors must be trained in the Vessel Inventory Application, the relevant PVS forms and the acceptance criteria applicable to their stage.',
          'Operatives/handlers must be briefed on tag recognition, escalation routes and the prohibition on moving, working on or releasing vessels outside of their current tag status.',
          'New personnel must not carry out any tagged-vessel activity until their training has been formally recorded and confirmed by their line manager and the Quality Manager.',
          'Re-training shall be provided following any material change to this policy, the tagging system or the Vessel Inventory Application.',
        ]}
      />
    </Section>

    <Section number="14" title="Document Control and Review">
      <Paragraph>
        This document is subject to the <strong>PVS Document Control Procedure</strong>. The
        master copy of this document is held in the PVS QMS repository. Printed copies are
        uncontrolled and must not be used as a reference for current procedures without
        verification against the master.
      </Paragraph>
      <Paragraph className="mt-2">This policy shall be reviewed:</Paragraph>
      <BulletList
        items={[
          'At intervals not exceeding 12 months from the date of issue or last review.',
          'Following any significant change to the vessel-processing workflow, tagging system, or Vessel Inventory Application.',
          'Following any significant non-conformance arising from activities governed by this policy.',
        ]}
      />
      <Paragraph className="mt-6 text-center text-pvs-text-muted">
        <em>END OF DOCUMENT — PVS-POL-VT-001 Rev 1.0</em>
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default VesselTaggingPolicyDoc;
